/* =============================================================
   ShowcaseMockups.js — high-fidelity, self-contained "snapshots"
   of the gated product surfaces (Dashboard, SMM panel, Marketplace,
   Casino). Logged-out visitors can't reach the real screens, so we
   recreate them faithfully in JSX + CSS using the real labels and
   number formats. Pure presentational — no data, no side effects.
   All classes are styled in LandingPage.css under .mock-*.
   ============================================================= */
import React from 'react';

/* ---- Chrome frames ---------------------------------------- */

export const BrowserFrame = ({ label = 'app.phonestream.online/dashboard', accent = 'cyan', children }) => (
  <div className={`mock-browser accent-${accent}`} aria-hidden="true">
    <div className="mock-browser-bar">
      <span className="mock-dot r" />
      <span className="mock-dot y" />
      <span className="mock-dot g" />
      <span className="mock-url">
        <span className="mock-lock">🔒</span>
        {label}
      </span>
      <span className="mock-live">
        <i /> LIVE
      </span>
    </div>
    <div className="mock-browser-body">{children}</div>
  </div>
);

export const PhoneFrame = ({ accent = 'purple', children }) => (
  <div className={`mock-phone accent-${accent}`} aria-hidden="true">
    <div className="mock-phone-notch" />
    <div className="mock-phone-screen">{children}</div>
  </div>
);

/* ---- Tiny SVG primitives ---------------------------------- */

const Sparkline = ({ color = '#00e0ff', points = '0,18 8,12 16,15 24,7 32,10 40,4 48,8 56,2' }) => (
  <svg className="mock-spark" viewBox="0 0 56 20" preserveAspectRatio="none">
    <defs>
      <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.35" />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </linearGradient>
    </defs>
    <polygon points={`0,20 ${points} 56,20`} fill={`url(#sg-${color.replace('#', '')})`} />
    <polyline points={points} fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);

const Bars = ({ heights = [40, 62, 35, 80, 55, 90, 48, 70, 60, 95, 52, 78, 66, 88] }) => (
  <div className="mock-bars">
    {heights.map((h, i) => (
      <span key={i} style={{ height: `${h}%` }} />
    ))}
  </div>
);

/* ============================================================
   1) DASHBOARD
   ============================================================ */
export const DashboardMock = () => (
  <div className="mock-dash">
    {/* live ticker */}
    <div className="mock-ticker">
      <span className="mock-ticker-live"><i /> LIVE</span>
      {[
        ['Vault', '1,284.50', 'gold'],
        ['Escrow', '642.00', 'cyan'],
        ['NFTs', '3', 'white'],
        ['Stream Rev', '+418.20', 'green'],
        ['Orders 24h', '12', 'gold'],
      ].map(([l, v, c]) => (
        <span className="mock-ticker-cell" key={l}>
          <em>{l}</em>
          <b className={`c-${c}`}>{v}</b>
        </span>
      ))}
    </div>

    {/* KPI tiles */}
    <div className="mock-kpis">
      {[
        ['PlayVault balance', '$1,284.50', '▲ 6.4%', 'gold', '0,18 8,14 16,15 24,9 32,11 40,5 48,7 56,3'],
        ['SMM escrow', '$642.00', '▲ 2.1%', 'cyan', '0,16 8,17 16,12 24,14 32,8 40,10 48,6 56,5'],
        ['NFTs owned', '3', 'lifetime', 'purple', '0,19 8,19 16,12 24,12 32,6 40,6 48,3 56,3'],
        ['Streaming revenue', '$5,940', '▲ 11.8%', 'green', '0,18 8,13 16,14 24,8 32,9 40,5 48,4 56,2'],
      ].map(([label, val, delta, c, pts]) => (
        <div className="mock-kpi" key={label}>
          <span className="mock-kpi-label">{label}</span>
          <span className="mock-kpi-val">{val}</span>
          <span className={`mock-kpi-delta c-${c}`}>{delta}</span>
          <Sparkline
            color={{ gold: '#f4d03f', cyan: '#00e0ff', purple: '#b06bff', green: '#39ff88' }[c]}
            points={pts}
          />
        </div>
      ))}
    </div>

    {/* chart + profile */}
    <div className="mock-dash-row">
      <div className="mock-panel mock-chart">
        <div className="mock-panel-head">
          <span>Your spending · last 14 days</span>
          <b>$2,118.00</b>
        </div>
        <Bars />
      </div>
      <div className="mock-panel mock-profile">
        <div className="mock-panel-head"><span>Your account</span></div>
        {[
          ['Member since', 'Mar 2026'],
          ['Last login', '2h ago'],
          ['Spots', '4 of 6'],
          ['SMM discount', '20% off'],
        ].map(([k, v]) => (
          <div className="mock-profile-row" key={k}>
            <em>{k}</em>
            <b>{v}</b>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================
   2) SMM PANEL
   ============================================================ */
export const SmmPanelMock = () => (
  <div className="mock-smm">
    <div className="mock-smm-head">
      <span className="mock-smm-brand">PHONESTREAM <em>SMM</em></span>
      <span className="mock-smm-bal">Credits <b>642.00 USDT</b></span>
    </div>

    <div className="mock-chips">
      {['All', 'Instagram', 'YouTube', 'TikTok', 'Spotify', 'Telegram', 'X'].map((p, i) => (
        <span key={p} className={`mock-chip ${i === 1 ? 'active' : ''}`}>{p}</span>
      ))}
    </div>

    <div className="mock-smm-grid">
      <div className="mock-smm-form">
        <label>Service</label>
        <div className="mock-field">Instagram Followers — High Quality</div>
        <label>Link</label>
        <div className="mock-field muted">https://instagram.com/yourprofile</div>
        <div className="mock-smm-two">
          <div>
            <label>Quantity</label>
            <div className="mock-field">10,000</div>
          </div>
          <div>
            <label>Rate /1k</label>
            <div className="mock-field">$0.84</div>
          </div>
        </div>
        <button className="mock-smm-cta" type="button" tabIndex={-1}>Place Order</button>
      </div>

      <div className="mock-smm-summary">
        <span className="mock-summary-title">Order Summary</span>
        {[
          ['Quantity', '10,000'],
          ['Subtotal', '$8.40'],
          ['Holder discount', '−20%'],
        ].map(([k, v]) => (
          <div className="mock-sum-row" key={k}><em>{k}</em><b>{v}</b></div>
        ))}
        <div className="mock-sum-row total"><em>You pay</em><b>6.72 USDT</b></div>
        <div className="mock-order-status">
          <span className="dot ok" /> Order #48217 · Processing
          <span className="mock-progress"><i style={{ width: '64%' }} /></span>
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================
   3) MARKETPLACE
   ============================================================ */
export const MarketplaceMock = () => (
  <div className="mock-mkt">
    <div className="mock-mkt-stats">
      {[
        ['Mint price', '900 USDT', 'Presale active'],
        ['Total volume', '2.41M USDT', 'all-time'],
        ['Minted', '742 / 1,000', null],
        ['Presale', '100 / 100', 'sold out'],
      ].map(([l, v, tag]) => (
        <div className="mock-mkt-stat" key={l}>
          <em>{l}</em>
          <b>{v}</b>
          {tag && <span className="mock-tag">{tag}</span>}
          {l === 'Minted' && (
            <span className="mock-supply"><i style={{ width: '74.2%' }} /></span>
          )}
        </div>
      ))}
    </div>

    <div className="mock-mkt-grid">
      {[
        ['#412', 'a3F9'],
        ['#318', '7C2e'],
        ['#207', 'Bd44'],
        ['#155', '9aE1'],
        ['#089', '4f0c'],
        ['#021', 'cE77'],
      ].map(([id, owner], i) => (
        <div className={`mock-nft tone-${(i % 4) + 1}`} key={id}>
          <div className="mock-nft-art">
            {/* Real collection art ("PhoneStream Rig"); neon orb shows if it can't load */}
            <span className="mock-nft-orb" />
            <img
              src="/nfts/placeholder.png"
              alt=""
              className="mock-nft-img"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <div className="mock-nft-meta">
            <b>PhoneStream Rig</b>
            <em>{id}</em>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ============================================================
   4) CASINO
   ============================================================ */
const MinesTile = ({ kind }) => (
  <span className={`mt mt-${kind}`}>{kind === 'gem' ? '💎' : kind === 'mine' ? '💣' : ''}</span>
);

export const CasinoMock = () => (
  <div className="mock-casino">
    <div className="mock-casino-head">
      <span className="mock-casino-title">💣 Mines</span>
      <span className="mock-casino-mult">2.50×</span>
    </div>

    <div className="mock-mines">
      {[
        'gem', 'hidden', 'hidden', 'gem', 'hidden',
        'hidden', 'gem', 'hidden', 'hidden', 'hidden',
        'hidden', 'hidden', 'gem', 'hidden', 'mine',
        'gem', 'hidden', 'hidden', 'hidden', 'hidden',
        'hidden', 'gem', 'hidden', 'hidden', 'hidden',
      ].map((k, i) => (
        <MinesTile key={i} kind={k} />
      ))}
    </div>

    <div className="mock-casino-foot">
      <button className="mock-cashout" type="button" tabIndex={-1}>💰 Cash Out $25.00</button>
      <span className="mock-casino-bal">Balance: $1,284.50</span>
    </div>

    <div className="mock-game-strip">
      {['🚀 Crash', '🎰 Plinko', '🎯 Keno', '🎡 Roulette', '🃏 Blackjack', '🂡 Video Poker'].map((g) => (
        <span key={g} className="mock-game-pill">{g}</span>
      ))}
    </div>
  </div>
);

const Mockups = { BrowserFrame, PhoneFrame, DashboardMock, SmmPanelMock, MarketplaceMock, CasinoMock };
export default Mockups;
